import { FC, useEffect, useState } from 'react';
import type { QualidadeLote, QualidadePayload } from '@/types/dashboards';
import { textoDesvio, type FiltroDesvio, type FiltroStatus } from '@/utils/equiplexQualidade';
import { fmtData, fmtDias, fmtEspera, fmtUn } from '@/utils/painelFormat';
import { STATUS, corNeutra, type ModoTema } from '../chartTheme';
import PainelCard from '../PainelCard';
import StatusLoteBadge from './StatusLoteBadge';

const PAGINA = 50;

const OPCOES_STATUS: { valor: FiltroStatus; rotulo: string }[] = [
  { valor: 'todos', rotulo: 'Todos os status' },
  { valor: 'pendente', rotulo: 'Aguardando CQ' },
  { valor: 'rejeicao_pendente', rotulo: 'Rejeição sem destino' },
  { valor: 'liberada', rotulo: 'Liberado' },
  { valor: 'cancelada', rotulo: 'Cancelado' },
  { valor: 'sem_saldo', rotulo: 'Ficha aberta sem saldo' },
];

const OPCOES_DESVIO: { valor: FiltroDesvio; rotulo: string }[] = [
  { valor: 'todos', rotulo: 'Qualquer desvio' },
  { valor: 'atrasado', rotulo: 'Passou da previsão' },
  { valor: 'no_prazo', rotulo: 'Dentro da previsão' },
];

/** Saldo do lote no depósito do CQ (snapshot antigo: o que a ficha ainda não destinou). */
const saldoNoCq = (l: QualidadeLote): number => l.saldo_deposito ?? l.saldo_cq;

const campo = 'bg-surface-primary border border-theme-border rounded-lg px-3 py-1.5 text-sm text-content-primary';

interface Props {
  lotes: QualidadeLote[];
  payload: QualidadePayload;
  busca: string;
  onBusca: (v: string) => void;
  status: FiltroStatus;
  onStatus: (v: FiltroStatus) => void;
  faixa: string;
  onFaixa: (v: string) => void;
  desvio: FiltroDesvio;
  onDesvio: (v: FiltroDesvio) => void;
  modo: ModoTema;
}

const Tempo: FC<{ lote: QualidadeLote }> = ({ lote }) => {
  if (lote.dias_no_cq !== null) {
    return <span className="font-medium text-content-primary">{fmtEspera(lote.dias_no_cq)}</span>;
  }
  return <>{fmtDias(lote.prazo_dias)}</>;
};

/** Ícone + texto (nunca só a cor): atrasado, no prazo, ou quantos dias ainda faltam. */
const Desvio: FC<{ lote: QualidadeLote; modo: ModoTema }> = ({ lote, modo }) => {
  const texto = textoDesvio(lote);
  if (!texto) {
    const motivo = lote.tipo === 'frasco' && !lote.prazo_previsto ? 'Prazo de liberação do frasco ainda não definido' : undefined;
    return (
      <span className="text-content-muted" title={motivo}>
        —
      </span>
    );
  }
  const saiu = lote.prazo_dias !== null;
  const atrasado = lote.desvio === 'atrasado';
  const icone = atrasado ? 'error' : saiu ? 'check_circle' : 'schedule';
  const cor = atrasado ? STATUS.critico : saiu ? STATUS.bom : lote.desvio_dias === 0 ? STATUS.atencao : corNeutra(modo);
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs font-medium text-content-primary">
      <span aria-hidden className="material-symbols-outlined text-[16px] leading-none" style={{ color: cor }}>
        {icone}
      </span>
      {texto}
    </span>
  );
};

type PropsFiltros = Omit<Props, 'lotes' | 'modo'> & { total: number };

const Filtros: FC<PropsFiltros> = ({ payload, busca, onBusca, status, onStatus, faixa, onFaixa, desvio, onDesvio, total }) => (
  <div className="flex flex-wrap items-center gap-2">
    <input
      type="search"
      value={busca}
      onChange={(e) => onBusca(e.target.value)}
      placeholder="Lote, código ou descrição"
      aria-label="Buscar lote"
      className={`${campo} w-full sm:w-64`}
    />
    <select aria-label="Status" value={status} onChange={(e) => onStatus(e.target.value as FiltroStatus)} className={campo}>
      {OPCOES_STATUS.map((o) => (
        <option key={o.valor} value={o.valor}>
          {o.rotulo}
        </option>
      ))}
    </select>
    <select aria-label="Tempo de espera" value={faixa} onChange={(e) => onFaixa(e.target.value)} className={campo}>
      <option value="todas">Qualquer espera</option>
      {payload.faixas_espera.map((f) => (
        <option key={f.faixa} value={f.faixa}>
          Esperando {f.rotulo}
        </option>
      ))}
    </select>
    <select aria-label="Desvio da previsão" value={desvio} onChange={(e) => onDesvio(e.target.value as FiltroDesvio)} className={campo}>
      {OPCOES_DESVIO.map((o) => (
        <option key={o.valor} value={o.valor}>
          {o.rotulo}
        </option>
      ))}
    </select>
    <span className="text-xs text-content-muted" data-testid="qualidade-tabela-contagem">
      {fmtUn(total)} {total === 1 ? 'lote' : 'lotes'}
    </span>
  </div>
);

/** Um lote por linha: a visão em tabela de tudo que os gráficos resumem. */
const LotesTable: FC<Props> = (props) => {
  const { lotes, payload, modo, busca, status, faixa, desvio } = props;
  const [limite, setLimite] = useState(PAGINA);
  useEffect(() => setLimite(PAGINA), [lotes.length, busca, status, faixa, desvio]);
  const prazo = payload.prazo_liberacao?.acabado;

  return (
    <PainelCard
      testId="qualidade-tabela"
      titulo="Lotes no CQ"
      subtitulo={
        'Primeiro os lotes com saldo no CQ, do que espera há mais tempo (os com entrada antiga logo depois, mesmo fora dos meses escolhidos); depois os demais.' +
        (prazo ? ` Previsão de liberação = entrada no CQ + ${prazo} dias (produto acabado).` : '')
      }
    >
      <Filtros {...props} total={lotes.length} />
      <div className="overflow-x-auto -mx-5 px-5">
        <table className="w-full min-w-[1240px] text-sm">
          <thead>
            <tr className="border-b border-theme-border text-left text-2xs uppercase tracking-wide text-content-muted">
              <th className="py-2 pr-3 font-medium">Lote</th>
              <th className="py-2 pr-3 font-medium">SKU</th>
              <th className="py-2 pr-3 font-medium">Máquina</th>
              <th className="py-2 pr-3 font-medium">Entrada no CQ</th>
              <th className="py-2 pr-3 font-medium" title="Entrada no CQ + prazo de liberação (14 dias no produto acabado)">
                Previsão
              </th>
              <th className="py-2 pr-3 font-medium">Saída</th>
              <th className="py-2 pr-3 font-medium">Status</th>
              <th className="py-2 pr-3 text-right font-medium" title="Prazo de liberação, ou há quanto tempo espera">
                Prazo / espera
              </th>
              <th className="py-2 pr-3 font-medium" title="Se passou ou não da previsão de liberação">
                Desvio
              </th>
              <th className="py-2 pr-3 text-right font-medium">Quantidade</th>
              <th className="py-2 pr-3 text-right font-medium">Rejeitada</th>
              <th className="py-2 text-right font-medium" title="Saldo do lote no depósito do CQ (CQ e CQV), como no CE0811">
                Saldo no CQ
              </th>
            </tr>
          </thead>
          <tbody className="tabular-nums">
            {lotes.slice(0, limite).map((l) => (
              <tr key={l.ficha} className="border-b border-theme-border hover:bg-surface-hover">
                <td className="py-2 pr-3 whitespace-nowrap">
                  <div className="font-medium text-content-primary">{l.lote || '—'}</div>
                  <div className="text-xs text-content-muted">OP {l.op}</div>
                </td>
                <td className="py-2 pr-3 max-w-[260px]">
                  <div className="text-content-primary">{l.codigo_datasul}</div>
                  <div className="truncate text-xs text-content-muted" title={payload.itens[l.codigo_datasul]?.descricao}>
                    {payload.itens[l.codigo_datasul]?.descricao || 'Item sem cadastro'}
                  </div>
                </td>
                <td className="py-2 pr-3 whitespace-nowrap text-content-secondary" title={l.grupo_maquina}>
                  {l.maquina}
                </td>
                <td className="py-2 pr-3 whitespace-nowrap">
                  {fmtData(l.entrada_cq)}
                  {l.fora_da_janela && <div className="text-2xs text-content-muted">entrada antiga</div>}
                </td>
                <td className="py-2 pr-3 whitespace-nowrap text-content-secondary">
                  {l.previsao_liberacao ? fmtData(l.previsao_liberacao) : '—'}
                </td>
                <td className="py-2 pr-3 whitespace-nowrap text-content-secondary">{l.saida_cq ? fmtData(l.saida_cq) : '—'}</td>
                <td className="py-2 pr-3">
                  <StatusLoteBadge lote={l} modo={modo} />
                </td>
                <td className="py-2 pr-3 text-right">
                  <Tempo lote={l} />
                </td>
                <td className="py-2 pr-3" data-testid={`desvio-${l.ficha}`}>
                  <Desvio lote={l} modo={modo} />
                </td>
                <td className="py-2 pr-3 text-right">{fmtUn(l.qt_original)}</td>
                <td className="py-2 pr-3 text-right text-content-secondary">{l.qt_rejeitada ? fmtUn(l.qt_rejeitada) : '—'}</td>
                <td className="py-2 text-right text-content-secondary">
                  {saldoNoCq(l) ? fmtUn(saldoNoCq(l)) : '—'}
                  {l.deposito === 'CQV' && saldoNoCq(l) > 0 && <div className="text-2xs text-content-muted">CQV · validação</div>}
                </td>
              </tr>
            ))}
            {lotes.length === 0 && (
              <tr>
                <td colSpan={12} className="py-8 text-center text-content-muted">
                  Nenhum lote com esses filtros.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {lotes.length > limite && (
        <button
          type="button"
          onClick={() => setLimite((v) => v + PAGINA)}
          className="self-center text-sm text-content-muted underline hover:text-content-primary"
        >
          Mostrar mais {fmtUn(Math.min(PAGINA, lotes.length - limite))} (faltam {fmtUn(lotes.length - limite)})
        </button>
      )}
    </PainelCard>
  );
};

export default LotesTable;
